import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLang } from "@/contexts/LangContext";
import { tr } from "@/lib/i18n";
import { ChevronLeft, ChevronRight, Trash2, Eye, FileText, Camera, Tag, Download, Search, ExternalLink, ArrowRight, CheckSquare, PlusCircle, User, Mail, RotateCcw } from "lucide-react";
import { FoodDialog } from "@/components/FoodDialog";
import { cn } from "@/lib/utils";
import { API_BASE, adminFetch } from "@/lib/api";
import { useLocation } from "wouter";

const PAGE_SIZE = 20;

export type RulingStatus = "allowed" | "forbidden" | "conditional" | "unknown" | "ambiguous" | "not_found";

interface HistoryItem {
  id: number;
  userId: string;
  userEmail?: string;
  userName?: string;
  query: string;
  analysisType: "text" | "image" | "label";
  /** Integer 0-100 or null when no score is available (ambiguous/not-found results) */
  compatibilityScore: number | null;
  createdAt: string;
  queryIntent?: string | null;
  /** Truthfully derived ruling from the backend — replaces the broken report.status approach */
  derivedRuling?: RulingStatus | null;
  resultMode?: string | null;
  needsClarification?: boolean;
  displayQuery?: string | null;
  selectedCandidate?: {
    type: string;
    id?: number | string;
    nameAr: string;
    reference: string;
  } | null;
  report: {
    explanation?: string;
    queryIntent?: string | null;
    resultMode?: string | null;
    needsClarification?: boolean;
    displayQuery?: string | null;
    selectedCandidate?: {
      type: string;
      id?: number | string;
      nameAr: string;
      reference: string;
    } | null;
    primaryRuling?: {
      status: "allowed" | "forbidden" | "conditional" | "unknown";
      nameAr: string;
    } | null;
    forbidden?: Array<{ name: string; nameAr?: string; reason?: string }>;
    conditional?: Array<{ name: string; nameAr?: string; reason?: string }>;
    allowed?: Array<{ name: string; nameAr?: string; reason?: string }>;
    unknown?: Array<{ name: string; nameAr?: string }>;
    matchedFoodId?: number;
    matchedDishId?: number;
  };
}

/** Maps raw queryIntent values to readable Arabic labels */
function intentLabel(intent: string | null | undefined): string {
  if (!intent) return "—";
  switch (intent.toUpperCase()) {
    case "FOOD": return "البحث عن طعام";
    case "DISH": return "البحث عن طبق";
    case "COMPOSITE": return "بحث مركّب";
    case "UNKNOWN": return "غير محدد";
    case "SPECIFIC_INGREDIENT": return "مكوّن محدد";
    case "BROAD_FOOD_CATEGORY": return "فئة غذائية عامة";
    case "SPECIFIC_DISH": return "طبق محدد";
    case "AMBIGUOUS_DISH_FOOD": return "بحث غير محدد";
    case "PRODUCT_BRAND": return "منتج تجاري";
    case "INGREDIENT_LIST": return "قائمة مكونات";
    case "FREE_FORM_MEAL": return "وصف وجبة";
    default: return intent; // Show raw value as safe fallback for unrecognized values
  }
}

/** Maps resultMode values to a clear Arabic result-type label */
function resultTypeLabel(resultMode: string | null | undefined, needsClarification?: boolean): string {
  if (needsClarification) return "يتطلب توضيحاً";
  if (!resultMode) return "—";
  switch (resultMode) {
    case "EXACT_FOOD": return "نتيجة محددة";
    case "GENERAL_RULE": return "قاعدة عامة";
    case "GENERAL_RULE_EXCEPTIONS": return "قاعدة عامة مع استثناءات";
    case "SPECIFIC_INHERITED": return "موروث من فئة";
    case "MIXED_CATEGORY": return "فئة مختلطة";
    case "COMPOSITE_FOOD": return "طعام مركّب";
    case "AMBIGUOUS_FOOD": return "خيارات متعددة";
    case "MULTIPLE_DISHES": return "أطباق متعددة";
    case "UNKNOWN_FOOD": return "غير معروف";
    case "NOT_FOUND": return "لم يُعثر عليه";
    case "LIST_ALLOWED": return "قائمة مسموحة";
    case "LIST_RESTRICTED": return "قائمة مقيدة";
    case "CATEGORY_REPORT": return "تقرير فئة";
    case "UNSUPPORTED_QUESTION": return "سؤال غير مدعوم";
    case "MULTI_ITEM": return "متعدد العناصر";
    default: return resultMode;
  }
}

/**
 * Derives a truthful ruling from the AnalysisReport JSON.
 * Operates on both the backend-derived field and the raw report as a resilient client-side fallback.
 */
function deriveRulingFromReport(rep: any): RulingStatus {
  if (!rep) return "unknown";

  if (typeof rep === "string") {
    try {
      rep = JSON.parse(rep);
    } catch {
      return "unknown";
    }
  }

  if (typeof rep !== "object" || rep === null) return "unknown";

  // 1. Ambiguous or multiple-dish results
  const resultMode: string = String(rep.resultMode || "");
  if (
    resultMode === "AMBIGUOUS_FOOD" ||
    resultMode === "MULTIPLE_DISHES" ||
    rep.needsClarification === true ||
    rep.isAmbiguous === true
  ) {
    return "ambiguous";
  }

  // 2. Not-found cases
  if (resultMode === "UNKNOWN_FOOD" || resultMode === "NOT_FOUND" || rep.notFound === true) {
    return "not_found";
  }

  // 3. primaryRuling is the authoritative ruling for a resolved food/dish
  if (rep.primaryRuling && rep.primaryRuling.status) {
    const s = String(rep.primaryRuling.status).toLowerCase().trim();
    if (s === "allowed" || s === "forbidden" || s === "conditional" || s === "unknown") return s as RulingStatus;
  }

  // 4. Ingredient-based derivation: forbidden > conditional > allowed > unknown
  const hasForbidden = Array.isArray(rep.forbidden) && rep.forbidden.length > 0;
  const hasConditional = Array.isArray(rep.conditional) && rep.conditional.length > 0;
  const hasAllowed = Array.isArray(rep.allowed) && rep.allowed.length > 0;
  if (hasForbidden) return "forbidden";
  if (hasConditional) return "conditional";
  if (hasAllowed) return "allowed";

  // 5. Decision engine / output fallback
  const finalDecision = rep.decisionEngine?.finalDecision || rep.decisionOutput?.finalDecision;
  if (typeof finalDecision === "string") {
    const fd = finalDecision.toLowerCase().trim();
    if (fd === "forbidden" || fd === "allowed" || fd === "conditional") return fd as RulingStatus;
  }

  // 6. Legacy status fallback
  if (typeof rep.status === "string") {
    const st = rep.status.toLowerCase().trim();
    if (st === "allowed" || st === "forbidden" || st === "conditional") return st as RulingStatus;
  }

  if (rep.compatibilityScore === null || rep.compatibilityScore === undefined) return "unknown";

  return "unknown";
}

function deriveRuling(item: HistoryItem | null | undefined): RulingStatus {
  if (!item) return "unknown";
  if (item.derivedRuling && item.derivedRuling !== "unknown") {
    return item.derivedRuling;
  }
  return deriveRulingFromReport(item.report);
}

function TypeIcon({ type }: { type: string }) {
  if (type === "text") return <FileText className="h-3.5 w-3.5" />;
  if (type === "image") return <Camera className="h-3.5 w-3.5" />;
  return <Tag className="h-3.5 w-3.5" />;
}

function TypeLabel({ type, lang }: { type: string; lang: "ar" | "en" }) {
  if (type === "text") return <span>{tr(lang, "text")}</span>;
  if (type === "image") return <span>{tr(lang, "image")}</span>;
  return <span>{tr(lang, "label")}</span>;
}

function ScoreBadge({ score }: { score: number | null | undefined }) {
  // score is an integer 0-100; null/undefined means no score is available (e.g. ambiguous result)
  if (score === null || score === undefined) {
    return (
      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 border-slate-200">
        غير متوفرة
      </span>
    );
  }
  const color = score >= 71 ? "bg-green-100 text-green-800 border-green-200" : score >= 51 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold", color)}>
      {score}%
    </span>
  );
}

function HistoryDetailDialog({ item, open, onClose, lang, onOpenAddFood }: {
  item: HistoryItem | null; open: boolean; onClose: () => void; lang: "ar" | "en"; onOpenAddFood?: (item: HistoryItem) => void;
}) {
  const [, setLocation] = useLocation();
  if (!item) return null;
  const { report } = item;

  // Derive display values from the correctly mapped fields (with client-side report fallback)
  const ruling = deriveRuling(item);
  const rmMode = item.resultMode || report.resultMode;
  const needsClari = item.needsClarification ?? report.needsClarification;
  const displayQ = item.displayQuery || report.displayQuery;
  const intent = item.queryIntent || report.queryIntent;
  const resolvedCandidate = item.selectedCandidate || report.selectedCandidate;

  const rulingLabel =
    ruling === "allowed" ? "مسموح" :
    ruling === "forbidden" ? "ممنوع" :
    ruling === "conditional" ? "مشروط" :
    ruling === "ambiguous" ? "لم يُحدّد طبق" :
    ruling === "not_found" ? "غير معروف" :
    "غير محدد";

  const handleOpenKnowledge = () => {
    onClose();
    if (report.matchedFoodId) {
      setLocation(`/foods?id=${report.matchedFoodId}`);
    } else {
      setLocation(`/foods?search=${encodeURIComponent(item.query)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TypeIcon type={item.analysisType} />
            <span className="truncate">{item.query}</span>
            <ScoreBadge score={item.compatibilityScore} />
          </DialogTitle>
          <DialogDescription>
            سجل تحليل وتدقيق بحث المستخدم (معرّف السجل: #{item.id})
          </DialogDescription>
        </DialogHeader>

        {/* QUERY PROCESSING FLOW */}
        <div className="space-y-4 text-sm text-right">
          <div className="p-3 bg-muted/50 rounded-lg border text-xs space-y-2">
            <span className="font-semibold text-foreground block border-b pb-1">مخطط معالجة البحث (Search Flow):</span>
            <div className="grid grid-cols-4 items-center text-muted-foreground gap-2 pt-1 text-center">
              <div className="bg-background p-2 rounded border">
                <span className="text-[10px] text-muted-foreground block">استعلام المستخدم</span>
                <span className="font-bold text-foreground text-xs truncate block">{item.query}</span>
                {displayQ && displayQ !== item.query && (
                  <span className="text-[9px] text-muted-foreground block truncate mt-0.5" title={displayQ}>
                    معروض: {displayQ}
                  </span>
                )}
              </div>
              <div className="bg-background p-2 rounded border">
                <span className="text-[10px] text-muted-foreground block">القصد المكتشف (Intent)</span>
                <span className="font-bold text-blue-700 text-xs block">
                  {intentLabel(intent)}
                </span>
                {intent && <span className="text-[9px] text-muted-foreground block font-mono">{intent}</span>}
              </div>
              <div className="bg-background p-2 rounded border">
                <span className="text-[10px] text-muted-foreground block">نوع النتيجة</span>
                <span className="font-bold text-purple-700 text-xs block">
                  {resultTypeLabel(rmMode, needsClari)}
                </span>
                {rmMode && <span className="text-[9px] text-muted-foreground block font-mono">{rmMode}</span>}
              </div>
              <div className="bg-background p-2 rounded border">
                <span className="text-[10px] text-muted-foreground block">الحكم والنتيجة</span>
                <span className={`font-bold text-xs block ${
                  ruling === "allowed" ? "text-green-700" :
                  ruling === "forbidden" ? "text-red-700" :
                  ruling === "conditional" ? "text-amber-700" :
                  "text-slate-600"
                }`}>{rulingLabel}</span>
              </div>
            </div>
            {resolvedCandidate && (
              <div className="text-[11px] text-muted-foreground pt-1 border-t">
                المرشح المختار:&nbsp;
                <span className="font-medium text-foreground">
                  {resolvedCandidate.nameAr || resolvedCandidate.reference}
                </span>
                {resolvedCandidate.reference && resolvedCandidate.reference !== resolvedCandidate.nameAr && (
                  <span className="text-muted-foreground"> ({resolvedCandidate.reference})</span>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground bg-background p-3 rounded border">
            <div>
              <span className="font-medium text-foreground">المستخدم / البريد:</span>
              <p className="font-semibold mt-0.5 text-foreground">{item.userName ? `${item.userName} (${item.userEmail || "غير متوفر"})` : (item.userEmail || "غير متوفر")}</p>
            </div>
            <div>
              <span className="font-medium text-foreground">{tr(lang, "date")}:</span>
              <p className="mt-0.5 text-foreground">{new Date(item.createdAt).toLocaleString("ar-EG")}</p>
            </div>
          </div>

          {report.explanation && (
            <div className="p-3 bg-blue-50/50 rounded border border-blue-100 text-xs space-y-1">
              <span className="font-semibold text-blue-900 block">التفسير والشرح:</span>
              <p className="text-blue-950 leading-relaxed">{report.explanation}</p>
            </div>
          )}

          {/* INGREDIENT BREAKDOWNS */}
          {report.forbidden && report.forbidden.length > 0 && (
            <div>
              <p className="font-semibold text-destructive mb-2">{tr(lang, "forbidden")} ({report.forbidden.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {report.forbidden.map((ing, idx) => (
                  <span key={idx} className="rounded-full border bg-red-50 px-2.5 py-0.5 text-xs text-red-800 border-red-200">
                    {ing.nameAr || ing.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.conditional && report.conditional.length > 0 && (
            <div>
              <p className="font-semibold text-amber-600 mb-2">{tr(lang, "conditional")} ({report.conditional.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {report.conditional.map((ing, idx) => (
                  <span key={idx} className="rounded-full border bg-amber-50 px-2.5 py-0.5 text-xs text-amber-800 border-amber-200">
                    {ing.nameAr || ing.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.allowed && report.allowed.length > 0 && (
            <div>
              <p className="font-semibold text-green-700 mb-2">{tr(lang, "allowed")} ({report.allowed.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {report.allowed.map((ing, idx) => (
                  <span key={idx} className="rounded-full border bg-green-50 px-2.5 py-0.5 text-xs text-green-800 border-green-200">
                    {ing.nameAr || ing.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
          <Button
            onClick={() => {
              onClose();
              onOpenAddFood?.(item);
            }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          >
            <PlusCircle className="h-4 w-4" />
            إضافة طعام (Add Food)
          </Button>
          <Button onClick={handleOpenKnowledge} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
            <ExternalLink className="h-4 w-4" />
            فتح السجل المعرفي (Open Knowledge Record)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function History() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [userNameInput, setUserNameInput] = useState("");
  const [userEmailInput, setUserEmailInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedUserName, setDebouncedUserName] = useState("");
  const [debouncedUserEmail, setDebouncedUserEmail] = useState("");
  const [analysisTypeFilter, setAnalysisTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Debounce text inputs (search, userName, userEmail)
  useEffect(() => {
    const timer = setTimeout(() => {
      const cleanSearch = search.trim();
      const cleanName = userNameInput.trim();
      const cleanEmail = userEmailInput.trim();

      setDebouncedSearch(cleanSearch);
      setDebouncedUserName(cleanName);
      setDebouncedUserEmail(cleanEmail);
      setOffset(0);
    }, 350);

    return () => clearTimeout(timer);
  }, [search, userNameInput, userEmailInput]);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HistoryItem | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addFoodOpen, setAddFoodOpen] = useState(false);
  const [addFoodInitialQuery, setAddFoodInitialQuery] = useState("");

  const handleOpenAddFood = (item: HistoryItem) => {
    const target = (item.displayQuery || item.query || "").trim();
    setAddFoodInitialQuery(target);
    setAddFoodOpen(true);
  };

  const { toast } = useToast();
  const { lang } = useLang();

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (debouncedSearch) queryParams.append("search", debouncedSearch);
      if (debouncedUserName) queryParams.append("userName", debouncedUserName);
      if (debouncedUserEmail) queryParams.append("userEmail", debouncedUserEmail);
      if (analysisTypeFilter && analysisTypeFilter !== "all") queryParams.append("analysisType", analysisTypeFilter);
      if (statusFilter && statusFilter !== "all") queryParams.append("status", statusFilter);

      const res = await adminFetch(`${API_BASE}/api/admin/history?${queryParams}`);
      const data = await res.json();
      if (data.items) {
        const enriched = data.items.map((i: HistoryItem) => ({
          ...i,
          derivedRuling: deriveRuling(i),
        }));
        setItems(enriched);
        setTotalItems(data.totalItems || data.items.length);
      } else if (Array.isArray(data)) {
        const enriched = data.map((i: HistoryItem) => ({
          ...i,
          derivedRuling: deriveRuling(i),
        }));
        setItems(enriched);
        setTotalItems(data.length);
      }
    } catch (err: any) {
      toast({ title: "خطأ", description: err?.message || "تعذر جلب سجل التحليلات", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [offset, debouncedSearch, debouncedUserName, debouncedUserEmail, analysisTypeFilter, statusFilter]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pageIds = items.map((i) => i.id);
      const unique = Array.from(new Set([...selectedIds, ...pageIds]));
      setSelectedIds(unique);
    } else {
      const pageIdsSet = new Set(items.map((i) => i.id));
      setSelectedIds(selectedIds.filter((id) => !pageIdsSet.has(id)));
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    }
  };

  const isAllPageSelected = items.length > 0 && items.every((i) => selectedIds.includes(i.id));

  const handleExportCsv = async () => {
    try {
      // Forward the same active filters so the CSV matches the filtered view
      const exportParams = new URLSearchParams();
      if (debouncedSearch) exportParams.append("search", debouncedSearch);
      if (debouncedUserName) exportParams.append("userName", debouncedUserName);
      if (debouncedUserEmail) exportParams.append("userEmail", debouncedUserEmail);
      if (analysisTypeFilter && analysisTypeFilter !== "all") exportParams.append("analysisType", analysisTypeFilter);
      if (statusFilter && statusFilter !== "all") exportParams.append("status", statusFilter);
      const qs = exportParams.toString();
      const res = await adminFetch(`${API_BASE}/api/admin/export/history${qs ? "?" + qs : ""}`);
      if (!res.ok) throw new Error("فشل تصدير البيانات");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `search_history_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: "خطأ", description: err?.message || "تعذر تصدير السجل", variant: "destructive" });
    }
  };

  const handleSingleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/history`, {
        method: "DELETE",
        body: JSON.stringify({ ids: [deleteTarget.id] }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: "تم الحذف بنجاح", description: `تم حذف تحليل "${deleteTarget.query}"` });
        setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget.id));
        setDeleteTarget(null);
        fetchHistory();
      } else {
        toast({ title: "خطأ في الحذف", description: data.error, variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "خطأ", description: err?.message, variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsDeleting(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/history`, {
        method: "DELETE",
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: "تم الحذف بنجاح", description: `تم حذف ${data.count || selectedIds.length} سجلات من سجل البحث` });
        setSelectedIds([]);
        setIsBulkDeleteOpen(false);
        fetchHistory();
      } else {
        toast({ title: "خطأ في الحذف الجماعي", description: data.error, variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "خطأ", description: err?.message, variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  };

  const hasPrev = offset > 0;
  const hasNext = offset + items.length < totalItems;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{tr(lang, "analysisHistory")}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            إدارة وتدقيق جميع استعلامات وبحوث المستخدمين على المنصة
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button variant="destructive" onClick={() => setIsBulkDeleteOpen(true)} className="gap-1.5">
              <Trash2 className="h-4 w-4" />
              حذف المحدد ({selectedIds.length})
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCsv} className="gap-2">
            <Download className="h-4 w-4" />
            تصدير CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="بحث بالاستعلام (Query)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setDebouncedSearch(search.trim());
                      setOffset(0);
                    }
                  }}
                  className="pr-9"
                />
              </div>

              <div className="relative w-full sm:w-[190px]">
                <User className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="اسم المستخدم..."
                  value={userNameInput}
                  onChange={(e) => setUserNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setDebouncedUserName(userNameInput.trim());
                      setOffset(0);
                    }
                  }}
                  className="pr-9"
                />
              </div>

              <div className="relative w-full sm:w-[210px]">
                <Mail className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="البريد الإلكتروني..."
                  value={userEmailInput}
                  onChange={(e) => setUserEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setDebouncedUserEmail(userEmailInput.trim());
                      setOffset(0);
                    }
                  }}
                  className="pr-9"
                  dir="ltr"
                />
              </div>

              <Select value={analysisTypeFilter} onValueChange={(val) => { setAnalysisTypeFilter(val); setOffset(0); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="نوع البحث" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأنواع</SelectItem>
                  <SelectItem value="text">نص (Text)</SelectItem>
                  <SelectItem value="image">صورة (Image)</SelectItem>
                  <SelectItem value="label">ملصق (Label)</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setOffset(0); }}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="الحكم / النتيجة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأحكام</SelectItem>
                  <SelectItem value="allowed">مسموح</SelectItem>
                  <SelectItem value="forbidden">ممنوع</SelectItem>
                  <SelectItem value="conditional">مشروط</SelectItem>
                  <SelectItem value="ambiguous">لم يُحدّد طبق (غامض)</SelectItem>
                  <SelectItem value="not_found">لم يُعثر عليه</SelectItem>
                  <SelectItem value="unknown">غير محدد</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="secondary" onClick={() => {
                setDebouncedSearch(search.trim());
                setDebouncedUserName(userNameInput.trim());
                setDebouncedUserEmail(userEmailInput.trim());
                setOffset(0);
              }}>
                بحث
              </Button>

              {(search.trim() || userNameInput.trim() || userEmailInput.trim() || analysisTypeFilter !== "all" || statusFilter !== "all") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearch("");
                    setUserNameInput("");
                    setUserEmailInput("");
                    setDebouncedSearch("");
                    setDebouncedUserName("");
                    setDebouncedUserEmail("");
                    setAnalysisTypeFilter("all");
                    setStatusFilter("all");
                    setOffset(0);
                  }}
                  className="text-muted-foreground hover:text-foreground gap-1.5 h-9"
                  title="إعادة ضبط جميع الفلاتر"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  إعادة ضبط
                </Button>
              )}
            </div>
          </div>

          {/* SELECTION BAR */}
          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between bg-primary/5 p-3 rounded-lg border border-primary/20 text-xs">
              <span className="font-semibold text-primary">
                تم تحديد {selectedIds.length} سجلات
              </span>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-7 text-xs text-muted-foreground hover:text-foreground">
                إلغاء التحديد
              </Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right">
              <thead>
                <tr className="border-b bg-muted/30 text-muted-foreground text-xs uppercase">
                  <th className="px-4 py-3 w-10 text-center">
                    <Checkbox
                      checked={isAllPageSelected}
                      onCheckedChange={(c) => handleSelectAll(!!c)}
                    />
                  </th>
                  <th className="px-4 py-3">التاريخ</th>
                  <th className="px-4 py-3">المستخدم / البريد</th>
                  <th className="px-4 py-3">البحث (Query)</th>
                  <th className="px-4 py-3">القصد (Intent)</th>
                  <th className="px-4 py-3">نوع التحليل</th>
                  <th className="px-4 py-3">نوع النتيجة</th>
                  <th className="px-4 py-3">الحكم</th>
                  <th className="px-4 py-3">درجة التوافق</th>
                  <th className="px-4 py-3 text-left">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">
                      {tr(lang, "noHistory")}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const isSelected = selectedIds.includes(item.id);
                    // Truthfully derived ruling from backend or report fallback
                    const ruling = deriveRuling(item);
                    const resolvedCandidate = item.selectedCandidate || item.report?.selectedCandidate;
                    const intent = item.queryIntent || item.report?.queryIntent;
                    const rmMode = item.resultMode || item.report?.resultMode;
                    const needsClari = item.needsClarification ?? item.report?.needsClarification;
                    return (
                      <tr key={item.id} className={cn("hover:bg-muted/20 transition-colors", isSelected && "bg-primary/5")}>
                        <td className="px-4 py-3 w-10 text-center">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(c) => handleSelectRow(item.id, !!c)}
                          />
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">
                          {new Date(item.createdAt).toLocaleDateString("ar-EG")}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground max-w-[200px]">
                          {item.userName ? (
                            <div>
                              <p className="font-semibold text-foreground truncate" title={item.userName}>{item.userName}</p>
                              <p className="text-xs text-muted-foreground truncate" title={item.userEmail}>{item.userEmail || "غير متوفر"}</p>
                            </div>
                          ) : (
                            <p className="text-foreground truncate" title={item.userEmail}>{item.userEmail || "غير متوفر"}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-xs font-semibold">
                          <p className="truncate">{item.query}</p>
                          {resolvedCandidate && (
                            <p className="text-[11px] text-muted-foreground font-normal truncate mt-0.5">
                              {resolvedCandidate.reference || resolvedCandidate.nameAr}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] bg-slate-100 text-slate-800 border" title={intent || undefined}>
                            {intentLabel(intent)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                            <TypeIcon type={item.analysisType} />
                            <TypeLabel type={item.analysisType} lang={lang} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] border ${
                            needsClari ? "bg-blue-50 text-blue-800 border-blue-200" :
                            rmMode === "AMBIGUOUS_FOOD" || rmMode === "MULTIPLE_DISHES" ? "bg-orange-50 text-orange-800 border-orange-200" :
                            rmMode === "UNKNOWN_FOOD" || rmMode === "NOT_FOUND" ? "bg-slate-100 text-slate-600 border-slate-200" :
                            rmMode === "EXACT_FOOD" || rmMode === "SPECIFIC_INHERITED" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                            "bg-slate-100 text-slate-700 border-slate-200"
                          }`}>
                            {resultTypeLabel(rmMode, needsClari)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            ruling === "allowed" ? "bg-green-100 text-green-800" :
                            ruling === "forbidden" ? "bg-red-100 text-red-800" :
                            ruling === "conditional" ? "bg-amber-100 text-amber-800" :
                            ruling === "ambiguous" ? "bg-orange-100 text-orange-800" :
                            ruling === "not_found" ? "bg-slate-100 text-slate-600" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {ruling === "allowed" ? "مسموح" :
                             ruling === "forbidden" ? "ممنوع" :
                             ruling === "conditional" ? "مشروط" :
                             ruling === "ambiguous" ? "لم يُحدّد طبق" :
                             ruling === "not_found" ? "غير معروف" :
                             "غير محدد"}
                          </span>
                        </td>
                        <td className="px-4 py-3"><ScoreBadge score={item.compatibilityScore} /></td>
                        <td className="px-4 py-3 text-left space-x-1 space-x-reverse whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="إضافة طعام من هذا البحث"
                            onClick={() => handleOpenAddFood(item)}
                          >
                            <PlusCircle className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedItem(item)} title="عرض التفاصيل">
                            <Eye className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-red-50"
                            onClick={() => setDeleteTarget(item)} title="حذف">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-muted-foreground">
              عرض {offset + 1}–{offset + items.length} من أصل {totalItems} سجل
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={!hasPrev}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                <ChevronRight className="h-4 w-4 ml-1" />
                {tr(lang, "prev")}
              </Button>
              <Button variant="outline" size="sm" disabled={!hasNext}
                onClick={() => setOffset(offset + PAGE_SIZE)}>
                {tr(lang, "next")}
                <ChevronLeft className="h-4 w-4 mr-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <HistoryDetailDialog
        item={selectedItem}
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        lang={lang}
        onOpenAddFood={handleOpenAddFood}
      />

      <FoodDialog
        open={addFoodOpen}
        onClose={() => setAddFoodOpen(false)}
        initialNameAr={addFoodInitialQuery}
        onSaved={() => {
          fetchHistory();
        }}
      />

      {/* SINGLE DELETE CONFIRM DIALOG */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>حذف هذا السجل؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف تحليل "{deleteTarget?.query}" نهائياً من سجلات البحث.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr(lang, "cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90"
              onClick={handleSingleDelete} disabled={isDeleting}>
              {tr(lang, "delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* BULK DELETE CONFIRM DIALOG */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف الجماعي</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف {selectedIds.length} سجلات من سجل البحث؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tr(lang, "cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90"
              onClick={handleBulkDelete} disabled={isDeleting}>
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
